import { tr, getLang } from "../../i18n";

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function locale() {
  return getLang() === "sv" ? "sv-SE" : "en-GB";
}

export function formatNumber(n) {
  return (Number(n) || 0).toLocaleString(locale());
}

/** 99.2 → "99,2 %" (sv) / "99.2%" (en). null → "–". */
export function formatPct(pct) {
  if (pct == null || !Number.isFinite(Number(pct))) return "–";
  const v = Number(pct).toLocaleString(locale(), { maximumFractionDigits: 1 });
  return getLang() === "sv" ? `${v} %` : `${v}%`;
}

/** 200 000 ms → "3 min", 5 h 20 min, 2 d 4 h. */
export function formatDuration(ms) {
  const v = Math.max(0, Number(ms) || 0);
  if (v < HOUR) return tr("{n} min", { n: Math.max(1, Math.round(v / MIN)) });
  if (v < DAY) {
    const h = Math.floor(v / HOUR);
    const m = Math.round((v % HOUR) / MIN);
    return m ? tr("{h} h {m} min", { h, m }) : tr("{h} h", { h });
  }
  const d = Math.floor(v / DAY);
  const h = Math.round((v % DAY) / HOUR);
  return h ? tr("{d} d {h} h", { d, h }) : tr("{d} d", { d });
}

export function formatAgo(ts, now = Date.now()) {
  if (!ts) return tr("Never");
  const diff = now - Number(ts);
  if (diff < MIN) return tr("Just now");
  return tr("{time} ago", { time: formatDuration(diff) });
}

export function formatDateTime(ts) {
  if (!ts) return "–";
  return new Date(ts).toLocaleString(locale(), { dateStyle: "short", timeStyle: "short" });
}

export function formatTime(ts) {
  return new Date(ts).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
}

export function formatWeekday(date) {
  return date.toLocaleDateString(locale(), { weekday: "short", day: "numeric", month: "short" });
}

/** Status level for an uptime percentage (good ≥ 99, warning ≥ 95). */
export function uptimeLevel(pct) {
  if (pct == null) return "none";
  if (pct >= 99) return "good";
  if (pct >= 95) return "warning";
  return "critical";
}

/**
 * Split status segments into one row per local day, newest day first.
 * Each part has left/width in % of the day. Time outside [from, now] is left empty.
 * @returns {Array<{date: Date, parts: Array<{left, width, state, from, to}>}>}
 */
export function splitTimelineDays(segments, from, now) {
  const rows = [];
  const first = new Date(from);
  first.setHours(0, 0, 0, 0);
  for (let day = new Date(first); day.getTime() < now; day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)) {
    const start = day.getTime();
    const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime();
    const len = end - start;
    const parts = [];
    for (const seg of segments || []) {
      const s = Math.max(seg.from, start);
      const e = Math.min(seg.to, end);
      if (e <= s) continue;
      parts.push({
        state: seg.state,
        from: s,
        to: e,
        left: ((s - start) / len) * 100,
        width: ((e - s) / len) * 100,
      });
    }
    rows.push({ date: new Date(start), parts });
  }
  return rows.reverse();
}
